import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Ruler, Check, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useShop } from "../context/ShopContext";
import { SizeChart } from "../components/SizeChart";

export default function ProductDetail() {
  const { slug } = useParams();
  const { addToCart, fitProfile } = useShop();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [color, setColor] = useState(null);
  const [fabric, setFabric] = useState(null);
  const [showChart, setShowChart] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setColor(r.data.colors[0]);
      setFabric(r.data.fabrics[0]);
      setActiveImg(0);
    });
  }, [slug]);

  if (!product) return <div className="pt-40 text-center text-charcoal/50" data-testid="product-loading">Loading…</div>;

  const hasFit = !!fitProfile;

  const handleAdd = () => {
    addToCart({
      slug: product.slug,
      name: product.name,
      price: product.price,
      currency: product.currency,
      image: product.images[0],
      color: color?.name,
      fabric: fabric?.name,
      size: hasFit ? fitProfile.recommended_size : "Standard",
      madeToMeasure: hasFit,
      qty: 1,
    });
    toast.success(`${product.name} added to your bag`, {
      description: hasFit ? `Tailored to your fit profile · Size ${fitProfile.recommended_size}` : "Complete your Fit Studio for a made-to-measure cut",
    });
  };

  return (
    <div data-testid="product-detail-page" className="pt-20">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid lg:grid-cols-2 gap-12">
        {/* Gallery */}
        <div className="flex flex-col-reverse lg:flex-row gap-4">
          <div className="flex lg:flex-col gap-3">
            {product.images.map((img, i) => (
              <button
                key={i}
                data-testid={`thumb-${i}`}
                onClick={() => setActiveImg(i)}
                className={`w-20 h-24 overflow-hidden rounded-lg border ${activeImg === i ? "border-charcoal" : "border-transparent opacity-60"}`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
          <div className="flex-1 aspect-[3/4] overflow-hidden rounded-2xl bg-linen">
            <img src={product.images[activeImg]} alt={product.name} className="w-full h-full object-cover" />
          </div>
        </div>

        {/* Info */}
        <div className="lg:py-6">
          <div className="overline text-sunkissed mb-3">{product.category.replace("-", " ")} · Made to Measure</div>
          <h1 className="font-serif text-4xl md:text-5xl font-light">{product.name}</h1>
          <p className="text-charcoal/60 mt-3">{product.tagline}</p>
          <div className="font-sans text-2xl mt-5">{product.currency} {product.price}</div>

          <p className="text-charcoal/70 leading-relaxed mt-7">{product.description}</p>

          {/* Fabric */}
          <div className="mt-9">
            <div className="overline mb-3">Fabric — {fabric?.name}</div>
            <div className="flex flex-wrap gap-3">
              {product.fabrics.map((f) => (
                <button
                  key={f.name}
                  data-testid={`fabric-${f.name}`}
                  onClick={() => setFabric(f)}
                  className={`text-left px-4 py-3 border transition-colors max-w-[220px] ${
                    fabric?.name === f.name ? "border-charcoal bg-linen" : "border-charcoal/20 hover:border-charcoal/50"
                  }`}
                >
                  <div className="text-sm font-medium">{f.name}</div>
                  <div className="text-xs text-charcoal/50 mt-0.5">{f.note}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Color */}
          <div className="mt-8">
            <div className="overline mb-3">Colour — {color?.name}</div>
            <div className="flex gap-3">
              {product.colors.map((c) => (
                <button
                  key={c.name}
                  data-testid={`color-${c.name}`}
                  onClick={() => setColor(c)}
                  title={c.name}
                  className={`w-10 h-10 rounded-full border-2 transition-transform ${
                    color?.name === c.name ? "border-charcoal scale-110" : "border-charcoal/15"
                  }`}
                  style={{ backgroundColor: c.hex }}
                />
              ))}
            </div>
          </div>

          {/* Fit box */}
          <div className="mt-9 p-5 bg-linen/70 border border-charcoal/10 rounded-2xl flex items-start gap-4" data-testid="fit-box">
            <Ruler size={20} strokeWidth={1.4} className="mt-0.5 shrink-0" />
            {hasFit ? (
              <div>
                <div className="text-sm font-medium flex items-center gap-2">
                  <Check size={16} className="text-sunkissed" /> Tailored to your fit profile
                </div>
                <p className="text-xs text-charcoal/60 mt-1">
                  Recommended size <strong>{fitProfile.recommended_size}</strong>. {product.fit_note}
                </p>
              </div>
            ) : (
              <div>
                <div className="text-sm font-medium">{product.fit_note}</div>
                <Link to="/fit-studio" data-testid="pdp-fit-link" className="text-xs text-sunkissed inline-flex items-center gap-1 mt-1 hover:gap-2 transition-all">
                  Create your fit profile <ArrowRight size={12} />
                </Link>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setShowChart((v) => !v)}
            data-testid="size-guide-toggle"
            className="mt-6 text-sm text-charcoal/70 underline underline-offset-4 hover:text-charcoal transition-colors"
          >
            {showChart ? "Hide size chart" : "View size chart"}
          </button>
          {showChart && (
            <div className="mt-4" data-testid="pdp-size-chart">
              <SizeChart gender={product.gender} highlight={fitProfile?.recommended_size} />
            </div>
          )}

          <button
            onClick={handleAdd}
            data-testid="add-to-cart"
            className="mt-6 w-full bg-ocean text-sand py-5 rounded-full overline hover:opacity-90 transition-opacity"
          >
            Add to bag — {product.currency} {product.price}
          </button>
        </div>
      </div>
    </div>
  );
}
